/* © CNES */
import { Extent } from 'ol/extent';
import { fromExtent } from 'ol/geom/Polygon';
import { transform } from 'ol/proj';
import { postelGeoserverTypes } from '../../../assets/config/filters/PostelReferenciel';
import {
  Missions,
  refMissions,
} from '../../../assets/config/filters/Referenciel';
import linkMapView from '../../../assets/config/mapView/LinkMapView/LinkMapView.json';
import SourceMapView from '../../../assets/config/mapView/SourceMapView/SourceMapView.json';
import { ContextType } from '../../../context/ConfigContext';
import { ItemFilter } from '../../../models/Filter';
import { MapDetails } from '../../../models/MapDetails';
import { PostelGeoserverTypes } from '../../../models/PostelGeoserverTypes';
import {
  DATASET,
  GRID_CODE,
  IDENTIFIER,
  INSTRUMENT_MODE,
  ORBIT_ABSOLUTE,
  ORBIT_RELATIVE,
  PLATFORM,
  PPARAMETER,
  PROCESSING_LEVEL,
  PRODUCT_ACQUISITION_TYPE,
  PRODUCT_TYPE,
  VISIBILITY,
} from '../../shared/Constants';

/**
 * Format CQL Filter With Product Names.
 * @param productNames all product names to filter displayed polygons from.
 * @returns string the CQL Filter string.
 */
export const formatCQLFilterWithProductNames = (productNames: string[]) => {
  let CQLFilterString = '';

  const allFiltersStrings = productNames.map((productName) => {
    return `'${productName}'`;
  });

  CQLFilterString = 'name in (' + allFiltersStrings.join(', ') + ')';
  return CQLFilterString;
};

/**
 * Format CQL Filter With S1 Filter criteria to filter geoserver products name (ex : S1A_IW_SLC__1SDV_20230711T161222_20230711T161248_049378_05F013_81B7).
 * @param filterData all filter criteria to filter displayed polygons from.
 * @returns string the CQL Filter string.
 */
export const formatCQLFilterWithS1Criteria = (
  filterData: ItemFilter,
  context: ContextType
) => {
  let CQLFilterString = '';

  const filters = [
    {
      key: PLATFORM,
      format: (values: string) => `platform in (${values})`,
    },
    {
      key: INSTRUMENT_MODE,
      format: (values: string) => `mode in (${values})`,
    },
    {
      key: PRODUCT_TYPE,
      format: (values: string) => `type in (${values})`,
    },
  ];

  filters.forEach(({ key, format }) => {
    let selectedValues = filterData[key];

    if (Array.isArray(selectedValues) && selectedValues.length > 0) {
      CQLFilterString = appendCQLString(
        CQLFilterString,
        format(selectedValues.map((val: string) => `'${val}'`).join(', '))
      );
    }
  });

  if (filterData[IDENTIFIER]) {
    CQLFilterString = appendCQLString(
      CQLFilterString,
      `name ilike '%${filterData[IDENTIFIER]}%'`
    );
  }

  if (filterData[ORBIT_RELATIVE]) {
    const relOrbitFilter = `relorbit = ${filterData[ORBIT_RELATIVE]}`;
    CQLFilterString = appendCQLString(CQLFilterString, `${relOrbitFilter}`);
  }

  if (filterData[ORBIT_ABSOLUTE]) {
    const absOrbitFilter = `absorbit = ${filterData[ORBIT_ABSOLUTE]}`;
    CQLFilterString = appendCQLString(CQLFilterString, `${absOrbitFilter}`);
  }

  if (filterData[GRID_CODE]) {
    const tileIdentifierFilter = `tile = ${addFirstLetterInTileIndentifier(
      filterData[GRID_CODE] as string
    )}`;
    CQLFilterString = appendCQLString(CQLFilterString, tileIdentifierFilter);
  }

  if (context.searchZone.value) {
    let polygon = 'POLYGON((';
    context.searchZone.value.features[0].geometry.coordinates[0].forEach(
      (latLong) => {
        polygon += `${transform(latLong, 'EPSG:4326', 'EPSG:3857')[0]} ${
          transform(latLong, 'EPSG:4326', 'EPSG:3857')[1]
        },`;
      }
    );
    polygon = polygon.slice(0, -1) + '))';

    CQLFilterString = appendCQLString(
      `(${CQLFilterString})`,
      `INTERSECTS(the_geom, ${polygon})`
    );
  }

  if (context.drawnShape) {
    const drawnShapeFilter = buildDrawnShapeExtentToPolygon(context.drawnShape);
    CQLFilterString = appendCQLString(`(${CQLFilterString})`, drawnShapeFilter);
  }
  return CQLFilterString;
};

/**
 * Prepare MapViewContainer Inputs.
 * @returns inputs for MapViewContainers.
 */
export const prepareMapViewContainerInputs = (
  changeSourceToOSM: Function,
  changeSource: (attributions: string, entry: string) => void
) => {
  return Object.entries(SourceMapView).map((entry) => {
    let inputs: MapDetails = {
      provider: entry[1].provider,
      label: entry[1].label,
      links:
        linkMapView[
          `${entry[1].links}` as
            | 'linksWSM'
            | 'linksOSM'
            | 'linksWSR'
            | 'linksCartoDark'
        ],
      clickFunction: () => {
        if (entry[0] === 'OSM') {
          return changeSourceToOSM();
        } else {
          return changeSource(entry[1].attributions, entry[1].url);
        }
      },
    };
    return inputs;
  });
};

/**
 * Get CQL Filter By Platform.
 * @param satellitePlatform filterdata platforms selected by user.
 * @param allSatellitePlatform all platforms for given collection.
 * @returns CQL string.
 */
export const getCQLFilterByPlatform = (
  satellitePlatform: string[] | string | null,
  allSatellitePlatform: string[]
) => {
  let CQLFilterByPlatform: string[] = [];
  if (satellitePlatform === null) {
    CQLFilterByPlatform = allSatellitePlatform.map((platform) => {
      return `'${platform}'`;
    });
  } else if (typeof satellitePlatform === 'object') {
    CQLFilterByPlatform = satellitePlatform
      ?.filter((platform: string) => {
        return allSatellitePlatform.includes(platform);
      })
      .map((platform) => {
        return `'${platform}'`;
      });
  }
  return CQLFilterByPlatform;
};

/**
 * get CQL Filter By ProductLevel.
 * @param productLevel filterdata productLevel selected by user.
 * @param allProductLevel all productLevel for given collection.
 * @returns CQL string.
 */
export const getCQLFilterByProductLevel = (
  productLevel: string[] | string | null,
  allProductLevel: string[]
) => {
  let CQLFilterwithProductLevel: string[] = [];
  if (productLevel !== null) {
    const productLevels = productLevel as string[];
    CQLFilterwithProductLevel = productLevels
      .filter((productLevel: string) => {
        // Allow L3B-OSO if L3B is in allProductLevel (since we'll transform it)
        if (productLevel === 'L3B-OSO') {
          return allProductLevel.includes('L3B');
        }
        // Allow L2B-SNOW - keep as is
        if (productLevel === 'L2B-SNOW') {
          return allProductLevel.includes('L2B-SNOW') || allProductLevel.includes('L2B');
        }
        return allProductLevel.includes(productLevel);
      })
      .map((productLevel: string) => {
        // Transform L3B-OSO to L3B for the CQL query only
        if (productLevel === 'L3B-OSO') {
          return `'L3B'`;
        }
        // Keep L2B-SNOW as is (will be handled specially in formatCQLFilterWithCriteria)
        return `'${productLevel}'`;
      });
  } else {
    CQLFilterwithProductLevel = allProductLevel.map((productLevel: string) => {
      return `'${productLevel}'`;
    });
  }
  return CQLFilterwithProductLevel;
};

/**
 * get CQL Filter By ProductType.
 * @param productType filterdata productType selected by user.
 * @param allProductType all productType for given collection.
 * @returns CQL string.
 */
const getCQLFilterByProductType = (
  productType: string[] | string | null,
  allProductType: string[]
) => {
  let CQLFilterByProductType: string[] = [];

  if (productType && typeof productType === 'object') {
    CQLFilterByProductType = productType
      ?.filter((productType: string) => {
        return allProductType.includes(productType);
      })
      .map((productType) => {
        return `'${productType.toLowerCase()}'`;
      });
  } else {
    CQLFilterByProductType = allProductType.map((productType: string) => {
      return `'${productType}'`;
    });
  }
  return CQLFilterByProductType;
};

/**
 * get CQL Filter By Parameter.
 * @param parameter filterdata parameter selected by user.
 * @param allParameter all parameter for given collection.
 * @returns CQL string.
 */
const getCQLFilterByParameter = (
  parameter: string[] | string | null,
  allParameter: string[]
) => {
  let CQLFilterByParameter: string[] = [];

  if (parameter && typeof parameter === 'object') {
    CQLFilterByParameter = parameter
      ?.filter((parameter: string) => {
        return allParameter.includes(parameter);
      })
      .map((parameter) => {
        let paramString = `parameter like '%${
          postelGeoserverTypes[
            parameter.toUpperCase() as keyof PostelGeoserverTypes
          ]
        }`;

        if (parameter === 'Fcover') {
          paramString = `parameter like '%${parameter.toUpperCase()}%' or parameter like '%FVC`;
        }

        return paramString;
      });
  }
  return CQLFilterByParameter;
};

/**
 * Format CQL Filter With SPOT Filter criteria to filter geoserver products.
 * @param filterData all filter criteria to filter displayed polygons from.
 * @param context application context
 * @param allSatellitePlatform all productType for given collection.
 * @param allProductLevel  all productLevel for given collection.
 * @param layerName optional layer name to apply specific filters (ex: 'muscate:gdh_l2bl3b')
 * @returns string the CQL Filter string.
 */
export const formatCQLFilterWithCriteria = (
  filterData: ItemFilter,
  context: ContextType,
  allSatellitePlatform?: string[],
  allProductLevel?: string[],
  layerName?: string
) => {
  let CQLFilterString = '';
  let CQLFilterByPlatform: string[] = [];
  let CQLFilterwithProductLevel: string[] = [];

  if (allSatellitePlatform) {
    CQLFilterByPlatform = getCQLFilterByPlatform(
      filterData[PLATFORM],
      allSatellitePlatform
    );
    CQLFilterString = CQLFilterByPlatform.length
      ? 'platform in (' + CQLFilterByPlatform.join(',') + ')'
      : '';
  }

  if (allProductLevel) {
    CQLFilterwithProductLevel = getCQLFilterByProductLevel(
      filterData[PROCESSING_LEVEL],
      allProductLevel
    );

    // Gérer le cas spécial L2B-SNOW quand Landsat et Sentinel-2 sont tous deux sélectionnés
    // Landsat utilise level='L2B-SNOW', Sentinel-2 utilise level='L2B' + type='SNOW_MASK'
    const hasL2BSNOW = CQLFilterwithProductLevel.includes("'L2B-SNOW'");
    const hasBothSatellites = filterData[DATASET] &&
      Array.isArray(filterData[DATASET]) &&
      filterData[DATASET].some((ds: string) => ds.includes('SENTINEL2')) &&
      filterData[DATASET].some((ds: string) => ds.includes('LANDSAT'));

    if (hasL2BSNOW && hasBothSatellites && layerName === 'muscate:gdh_l2bl3b') {
      // Ajouter L2B au filtre level pour permettre les produits Sentinel-2
      if (!CQLFilterwithProductLevel.includes("'L2B'")) {
        CQLFilterwithProductLevel.push("'L2B'");
      }
    }

    if (CQLFilterwithProductLevel.length) {
      CQLFilterString = appendCQLString(
        CQLFilterString,
        'level in (' + CQLFilterwithProductLevel.join(',') + ')'
      );
    }
  }

  // Ajouter le filtre productType UNIQUEMENT pour la couche muscate:gdh_l2bl3b
  // Cette couche contient des produits L2B et L3B qui partagent les mêmes niveaux
  // et nécessitent un filtre sur le type pour les différencier
  // Types possibles : SNOW_MASK (pour L2B-SNOW et L3B), L2B_BIOPHY (pour L2B_BIOPHY), REFLECTANCE (pour L2B-WATER)
  if (layerName === 'muscate:gdh_l2bl3b') {
    const productTypeFromRelations: { [key: string]: string } = {
      'L2B-SNOW': 'SNOW_MASK',
      'L2B_BIOPHY': 'L2B_BIOPHY',
      'L2B-WATER': 'REFLECTANCE',
      'L3B': 'SNOW_MASK'
    };

    // Collecter tous les produits sélectionnés (depuis PROCESSING_LEVEL et PRODUCT_TYPE)
    const selectedProducts = new Set<string>();

    if (filterData[PROCESSING_LEVEL]) {
      const levels = Array.isArray(filterData[PROCESSING_LEVEL])
        ? filterData[PROCESSING_LEVEL]
        : [filterData[PROCESSING_LEVEL]];
      (levels as string[]).forEach(level => selectedProducts.add(level));
    }

    if (filterData[PRODUCT_TYPE]) {
      const types = Array.isArray(filterData[PRODUCT_TYPE])
        ? filterData[PRODUCT_TYPE]
        : [filterData[PRODUCT_TYPE]];
      (types as string[]).forEach(type => selectedProducts.add(type));
    }

    // Construire le filtre productType pour les produits qui en ont besoin
    const productTypes = new Set<string>();
    selectedProducts.forEach(product => {
      if (productTypeFromRelations[product]) {
        productTypes.add(productTypeFromRelations[product]);
      }
    });

    if (productTypes.size > 0) {
      const productTypeFilter = Array.from(productTypes).map(pt => `'${pt.toUpperCase()}'`).join(',');
      CQLFilterString = appendCQLString(
        CQLFilterString,
        `type in (${productTypeFilter})`
      );
    }
  }

  if (filterData[IDENTIFIER]) {
    CQLFilterString = appendCQLString(
      CQLFilterString,
      `name ilike '%${filterData[IDENTIFIER]}%'`
    );
  }

  if (filterData[ORBIT_RELATIVE]) {
    const relOrbitFilter = `relorbit = ${filterData[ORBIT_RELATIVE]}`;
    CQLFilterString = appendCQLString(CQLFilterString, relOrbitFilter);
  }

  if (filterData[ORBIT_ABSOLUTE]) {
    const absOrbitFilter = `absorbit = ${filterData[ORBIT_ABSOLUTE]}`;
    CQLFilterString = appendCQLString(CQLFilterString, absOrbitFilter);
  }

  if (filterData[GRID_CODE]) {
    const tileIdentifierFilter = `tile = ${addFirstLetterInTileIndentifier(
      filterData[GRID_CODE] as string
    )}`;
    CQLFilterString = appendCQLString(CQLFilterString, tileIdentifierFilter);
  }

  if (context.searchZone.value) {
    let polygon = 'POLYGON((';
    context.searchZone.value.features[0].geometry.coordinates[0].forEach(
      (latLong) => {
        polygon += `${transform(latLong, 'EPSG:4326', 'EPSG:3857')[0]} ${
          transform(latLong, 'EPSG:4326', 'EPSG:3857')[1]
        },`;
      }
    );
    polygon = polygon.slice(0, -1) + '))';
    CQLFilterString = appendCQLString(
      `(${CQLFilterString})`,
      `INTERSECTS(the_geom, ${polygon})`
    );
  }

  if (context.drawnShape) {
    if (CQLFilterString.length) {
      const drawnShapeFilter = buildDrawnShapeExtentToPolygon(
        context.drawnShape
      );
      CQLFilterString = appendCQLString(
        `(${CQLFilterString})`,
        drawnShapeFilter
      );
    } else {
      CQLFilterString = buildDrawnShapeExtentToPolygon(context.drawnShape);
    }
  }

  return CQLFilterString;
};

/**
 * Format CQL Filter With SPIRIT Filter criteria to filter geoserver products.
 * @param filterData all filter criteria to filter displayed polygons from.
 * @param context ContextType.
 * @returns string the CQL Filter string.
 */
export const formatCQLFilterWithSpirit = (
  filterData: ItemFilter,
  context: ContextType
) => {
  let CQLFilterString = '';

  if (
    (filterData[DATASET]?.includes('THEIA_SPIRIT_SPOT5_L1A') &&
      filterData[PLATFORM] === null) ||
    filterData[PLATFORM]?.includes('SPOT5')
  ) {
    CQLFilterString = `name ilike '%GES%' or name ilike '%SPI%'`;
  }

  if (filterData[IDENTIFIER]) {
    CQLFilterString = `name ilike '%${filterData[IDENTIFIER]}%'`;
  }

  if (filterData[ORBIT_RELATIVE]) {
    const relOrbitFilter = `relorbit = ${filterData[ORBIT_RELATIVE]}`;
    CQLFilterString = appendCQLString(CQLFilterString, relOrbitFilter);
  }

  if (filterData[ORBIT_ABSOLUTE]) {
    const absOrbitFilter = `absorbit = ${filterData[ORBIT_ABSOLUTE]}`;
    CQLFilterString = appendCQLString(CQLFilterString, absOrbitFilter);
  }

  if (filterData[GRID_CODE]) {
    const tileIdentifierFilter = `tile = ${addFirstLetterInTileIndentifier(
      filterData[GRID_CODE] as string
    )}`;
    CQLFilterString = appendCQLString(CQLFilterString, tileIdentifierFilter);
  }

  if (context.searchZone.value) {
    let polygon = 'POLYGON((';
    context.searchZone.value.features[0].geometry.coordinates[0].forEach(
      (latLong) => {
        polygon += `${transform(latLong, 'EPSG:4326', 'EPSG:3857')[0]} ${
          transform(latLong, 'EPSG:4326', 'EPSG:3857')[1]
        },`;
      }
    );
    polygon = polygon.slice(0, -1) + '))';

    CQLFilterString = appendCQLString(
      `(${CQLFilterString})`,
      `INTERSECTS(the_geom, ${polygon})`
    );
  }

  if (context.drawnShape) {
    if (CQLFilterString.length) {
      const drawnShapeFilter = buildDrawnShapeExtentToPolygon(
        context.drawnShape
      );
      CQLFilterString = appendCQLString(
        `(${CQLFilterString})`,
        drawnShapeFilter
      );
    } else {
      CQLFilterString = buildDrawnShapeExtentToPolygon(context.drawnShape);
    }
  }

  return CQLFilterString;
};

/**
 * Append CQL String in CQL.
 * @param CQLFilterString The existing CQL filter.
 * @param stringToAppend The new filter string to integrate in existing CQL.
 * @returns CQL string.
 */
const appendCQLString = (CQLFilterString: string, stringToAppend: string) => {
  if (CQLFilterString.length && CQLFilterString !== '()') {
    CQLFilterString = `${CQLFilterString} and ${stringToAppend}`;
  } else {
    CQLFilterString = `${stringToAppend}`;
  }

  return CQLFilterString;
};

/**
 * Apply visibility filter based on user authentication and VISIBILITY filter data.
 * @param CQLFilterString The existing CQL filter string.
 * @param context The application context containing user login info.
 * @param filterData The filter data containing VISIBILITY selection.
 * @param mission The mission type ('TRISHNA' or 'CO3D') to determine expert/private label.
 * @returns Updated CQL filter string with visibility criteria.
 *
 * Logic:
 * - Cas 1: User NOT logged in → visibility = 'public'
 * - Cas 2: User logged in WITHOUT expert rights (VISIBILITY undefined/null) → visibility = 'public'
 * - Cas 3: User logged in WITH expert rights (VISIBILITY = []) → visibility in ('public', 'expert'/'private')
 * - User logged in WITH expert rights and selection made (VISIBILITY = ['public'/'expert']) → filter by selection
 */
const applyVisibilityFilter = (
  CQLFilterString: string,
  context: ContextType,
  filterData: ItemFilter,
  mission: 'TRISHNA' | 'CO3D'
): string => {
  const expertLabel = mission === 'CO3D' ? 'private' : 'expert';

  if (!context.userLoggedIn?.email) {
    // Cas 1: Utilisateur non connecté → seulement public
    const visibilityFilter = `visibility = 'public'`;
    return appendCQLString(CQLFilterString, visibilityFilter);
  } else if (filterData[VISIBILITY] === undefined || filterData[VISIBILITY] === null) {
    // Cas 2: Utilisateur connecté SANS droits expert (pas de section Mode affichée) → seulement public
    const visibilityFilter = `visibility = 'public'`;
    return appendCQLString(CQLFilterString, visibilityFilter);
  } else if ((filterData[VISIBILITY] as string[]).length > 0) {
    // Utilisateur connecté AVEC section Mode et choix fait → filtrer selon le choix
    return appendCQLString(
      CQLFilterString,
      `visibility in (${(filterData[VISIBILITY] as string[])
        .map((value) => `'${value}'`)
        .join(',')
        .toLowerCase()})`
    );
  } else {
    // Cas 3: Utilisateur connecté AVEC droits expert mais rien sélectionné (section Mode visible, VISIBILITY = [])
    // → afficher public ET expert/private
    const visibilityFilter = `visibility in ('public', '${expertLabel}')`;
    return appendCQLString(CQLFilterString, visibilityFilter);
  }
};

/**
 * Format CQL Filter With SPOT Filter criteria to filter geoserver products.
 * @param filterData all filter criteria to filter displayed polygons from.
 * @returns string the CQL Filter string.
 */
export const formatCQLFilterWithPostel = (
  filterData: ItemFilter,
  context: ContextType,
  allProductType?: string[],
  allParameter?: string[]
) => {
  let CQLFilterString = '';
  let CQLFilterwithProductType: string[] = [];
  let CQLFilterwithParameter: string[] = [];

  if (allProductType) {
    CQLFilterwithProductType = getCQLFilterByProductType(
      filterData[PRODUCT_TYPE],
      allProductType
    );
    if (CQLFilterwithProductType.length) {
      CQLFilterString = appendCQLString(
        CQLFilterString,
        'type in (' + CQLFilterwithProductType.join(',') + ')'
      );
    }
  }

  if (allParameter) {
    CQLFilterwithParameter = getCQLFilterByParameter(
      filterData[PPARAMETER],
      allParameter
    );

    if (CQLFilterwithParameter?.length) {
      const CQLFilterWithParameterString = `(${CQLFilterwithParameter.map(
        (CQLWithParameter) => CQLWithParameter.concat(`%'`)
      ).join(' or ')})`;
      CQLFilterString = CQLFilterString?.length
        ? `(${CQLFilterString}) and ${CQLFilterWithParameterString}`
        : CQLFilterWithParameterString;
    }
  }

  // add instrument AVHRR when case of product GEOV2 AVHRR
  const refMission = refMissions.find((refMission) => {
    return (
      JSON.stringify(refMission.key) === JSON.stringify(filterData[DATASET])
    );
  });
  if (refMission?.value === Missions.GEOV2_AVHRR) {
    CQLFilterString = CQLFilterString?.length
      ? `${CQLFilterString} and instrument in ('AVHRR')`
      : `instrument in ('AVHRR')`;
  }

  if (filterData[IDENTIFIER]) {
    CQLFilterString = appendCQLString(
      CQLFilterString,
      `name ilike '%${filterData[IDENTIFIER]}%'`
    );
  }

  if (filterData[GRID_CODE]) {
    const tileIdentifierFilter = `tile = ${addFirstLetterInTileIndentifier(
      filterData[GRID_CODE] as string
    )}`;
    CQLFilterString = appendCQLString(CQLFilterString, tileIdentifierFilter);
  }

  if (context.searchZone.value) {
    let polygon = 'POLYGON((';
    context.searchZone.value.features[0].geometry.coordinates[0].forEach(
      (latLong) => {
        polygon += `${transform(latLong, 'EPSG:4326', 'EPSG:3857')[0]} ${
          transform(latLong, 'EPSG:4326', 'EPSG:3857')[1]
        },`;
      }
    );
    polygon = polygon.slice(0, -1) + '))';

    const searchZoneFilter = `INTERSECTS(the_geom, ${polygon})`;
    CQLFilterString = appendCQLString(`(${CQLFilterString})`, searchZoneFilter);
  }

  if (context.drawnShape) {
    const drawnShapeFilter = buildDrawnShapeExtentToPolygon(context.drawnShape);
    CQLFilterString = appendCQLString(`(${CQLFilterString})`, drawnShapeFilter);
  }

  return CQLFilterString;
};

/**
 * build DrawnShape Extent To Polygon string.
 * @param drawnShape Extent from drawn shape by user.
 * @returns partial CQL string.
 */
export const buildDrawnShapeExtentToPolygon = (drawnShape: Extent) => {
  const polygon = fromExtent(drawnShape);
  const polygonString = polygon
    .getCoordinates()
    .map(
      (latlongTable) => `${latlongTable.map((latlong) => latlong.join(' '))},`
    )
    .join()
    .slice(0, -1);
  return `INTERSECTS(the_geom, POLYGON((${polygonString})))`;
};

/**
 * Format CQL Filter With TRISHNA Filter criteria to filter geoserver products.
 * @param filterData all filter criteria to filter displayed polygons from.
 * @param context ContextType.
 * @param allProductLevel  all productLevel for given collection.
 * @param allProductType  all productType for given collection.
 * @returns string the CQL Filter string.
 */
export const formatCQLFilterWithTrishna = (
  filterData: ItemFilter,
  context: ContextType,
  allProductLevel?: string[],
  allProductType?: string[]
) => {
  let CQLFilterString = '';
  let CQLFilterwithProductLevel: string[] = [];
  let CQLFilterwithProductType: string[] = [];

  if (filterData[IDENTIFIER]) {
    CQLFilterString = `name ilike '%${filterData[IDENTIFIER]}%'`;
  }

  if (allProductLevel) {
    CQLFilterwithProductLevel = getCQLFilterByProductLevel(
      filterData[PROCESSING_LEVEL],
      allProductLevel
    );

    if (CQLFilterwithProductLevel.length) {
      CQLFilterString = appendCQLString(
        CQLFilterString,
        'level in (' + CQLFilterwithProductLevel.join(',') + ')'
      );
    }
  }

  if (allProductType) {
    // Pour Trishna, on préserve la casse originale (ex: "DEM" au lieu de "dem")
    if (filterData[PRODUCT_TYPE] && typeof filterData[PRODUCT_TYPE] === 'object') {
      CQLFilterwithProductType = (filterData[PRODUCT_TYPE] as string[])
        .filter((productType: string) => {
          return allProductType.includes(productType);
        })
        .map((productType) => {
          return `'${productType}'`;
        });
    } else {
      CQLFilterwithProductType = allProductType.map((productType: string) => {
        return `'${productType}'`;
      });
    }

    if (CQLFilterwithProductType.length) {
      CQLFilterString = appendCQLString(
        CQLFilterString,
        'type in (' + CQLFilterwithProductType.join(',') + ')'
      );
    }
  }

  if (filterData[ORBIT_RELATIVE]) {
    const relOrbitFilter = `relorbit = ${filterData[ORBIT_RELATIVE]}`;
    CQLFilterString = appendCQLString(CQLFilterString, relOrbitFilter);
  }

  if (filterData[ORBIT_ABSOLUTE]) {
    const absOrbitFilter = `absorbit = ${filterData[ORBIT_ABSOLUTE]}`;
    CQLFilterString = appendCQLString(CQLFilterString, absOrbitFilter);
  }

  if (filterData[GRID_CODE]) {
    const tileIdentifierFilter = `tile = ${addFirstLetterInTileIndentifier(
      filterData[GRID_CODE] as string
    )}`;
    CQLFilterString = appendCQLString(CQLFilterString, tileIdentifierFilter);
  }

  // Appliquer le filtre de visibilité (public/expert) pour TRISHNA
  CQLFilterString = applyVisibilityFilter(CQLFilterString, context, filterData, 'TRISHNA');

  if (filterData[PRODUCT_ACQUISITION_TYPE]) {
    CQLFilterString = appendCQLString(
      CQLFilterString,
      `acquisitiontype in (${(filterData[PRODUCT_ACQUISITION_TYPE] as string[])
        .map((value) => `'${value}'`)
        .join(',')
        .toLowerCase()})`
    );
  }

  if (context.searchZone.value) {
    let polygon = 'POLYGON((';
    context.searchZone.value.features[0].geometry.coordinates[0].forEach(
      (latLong) => {
        polygon += `${transform(latLong, 'EPSG:4326', 'EPSG:3857')[0]} ${
          transform(latLong, 'EPSG:4326', 'EPSG:3857')[1]
        },`;
      }
    );
    polygon = polygon.slice(0, -1) + '))';

    CQLFilterString = appendCQLString(
      `(${CQLFilterString})`,
      `INTERSECTS(the_geom, ${polygon})`
    );
  }

  if (context.drawnShape) {
    if (CQLFilterString.length) {
      const drawnShapeFilter = buildDrawnShapeExtentToPolygon(
        context.drawnShape
      );
      CQLFilterString = appendCQLString(
        `(${CQLFilterString})`,
        drawnShapeFilter
      );
    } else {
      CQLFilterString = buildDrawnShapeExtentToPolygon(context.drawnShape);
    }
  }

  return CQLFilterString;
};

export const formatCQLFilterWithCO3D = (
  filterData: ItemFilter,
  context: ContextType,
  allSatellitePlatform?: string[],
  allProductLevel?: string[],
) => {
  let CQLFilterString: string;

  CQLFilterString = formatCQLFilterWithCriteria(
    filterData,
    context,
    allSatellitePlatform,
    allProductLevel
  );

  // Ajouter le filtre productType pour CO3D selon les niveaux sélectionnés
  // L2A, L2C → type = 'REFLECTANCE'
  // L3, L4 → type = 'DEM'
  if (filterData[PROCESSING_LEVEL]) {
    const productTypeFromLevel: { [key: string]: string } = {
      'L2A': 'REFLECTANCE',
      'L2C': 'REFLECTANCE',
      'L3': 'DEM',
      'L4': 'DEM'
    };

    const levels = Array.isArray(filterData[PROCESSING_LEVEL])
      ? filterData[PROCESSING_LEVEL]
      : [filterData[PROCESSING_LEVEL]];

    const productTypes = new Set<string>();
    (levels as string[]).forEach(level => {
      if (productTypeFromLevel[level]) {
        productTypes.add(productTypeFromLevel[level]);
      }
    });

    if (productTypes.size > 0) {
      const productTypeFilter = Array.from(productTypes).map(pt => `'${pt}'`).join(',');
      CQLFilterString = appendCQLString(
        CQLFilterString,
        `type in (${productTypeFilter})`
      );
    }
  }

  // Appliquer le filtre de visibilité (public/private) pour CO3D
  CQLFilterString = applyVisibilityFilter(CQLFilterString, context, filterData, 'CO3D');

  if (filterData[PRODUCT_ACQUISITION_TYPE]) {
    CQLFilterString = appendCQLString(
      CQLFilterString,
      `acquisitiontype in (${(filterData[PRODUCT_ACQUISITION_TYPE] as string[])
        .map((value) => `'${value}'`)
        .join(',')
        .toLowerCase()})`
    );
  }

  return CQLFilterString;
};

/**
 * Remove 'T' as first letter if present and input value length is 6.
 * @param value Tile value string to evaluate
 * @returns string with 'T' prepended if length is 5, otherwise returns the original value
 */
export const addFirstLetterInTileIndentifier = (value: string) => {
  // If the value length is 5, prepend 'T' to it
  return value.length === 6 && value.startsWith('T')
    ? `'${value.slice(1)}'`
    : `'${value}'`;
};
